// Which beat of the tracked grid do the taps sit on? Taps are on beats, but the onset
// envelope may lock to the off-beat (bass on 2& and 4, conga on 4&). This file decides,
// from the taps, whether to keep the onset grid or move it half a beat, and says how sure
// it is. It depends on nothing else in the package (the bench copies it to run the same
// decision against older trackers).

/**
 * Offset of the taps from a beat grid, in beats: the circular mean of each tap's
 * fractional position between beats, in (-0.5, 0.5]. 0 means the taps sit on the
 * beats; ±0.5 means they sit exactly between them. `resultant` is the length of the
 * mean vector (1: all taps at the same offset; near 0: no consistent offset).
 */
export function tapOffset(beats: readonly number[], taps: readonly number[]): { offset: number; resultant: number } {
  let c = 0, s = 0;
  for (const t of taps) {
    const x = fractionalBeat(beats, t);
    c += Math.cos(2 * Math.PI * x);
    s += Math.sin(2 * Math.PI * x);
  }
  const n = Math.max(1, taps.length);
  return { offset: Math.atan2(s, c) / (2 * Math.PI), resultant: Math.hypot(c, s) / n };
}

/** The grid moved half a beat later: each beat becomes the midpoint of it and the next (the last one extrapolates). */
export function halfBeatShift(beats: readonly number[]): number[] {
  if (beats.length < 2) return [...beats];
  const shifted: number[] = [];
  for (let i = 0; i + 1 < beats.length; i++) shifted.push((beats[i]! + beats[i + 1]!) / 2);
  const last = beats.length - 1;
  shifted.push(beats[last]! + (beats[last]! - beats[last - 1]!) / 2);
  return shifted;
}

/** How well one grid hypothesis fits the taps. */
export interface HypothesisFit {
  /** Mean over taps of the distance to the nearest beat of this grid, in beats (0 to 0.5). */
  readonly meanResidual: number;
  /** Signed residual of each tap in beats: positive means the tap is later than its nearest beat. Same order as the taps. */
  readonly residuals: readonly number[];
}

export interface TapAlignment {
  /** The chosen grid: the input grid, or the input grid moved half a beat later. */
  readonly beats: number[];
  /** true: the half-beat shift fits the taps better and was chosen. */
  readonly shifted: boolean;
  /** The grid as tracked. */
  readonly grid: HypothesisFit;
  /** The grid moved half a beat later. */
  readonly half: HypothesisFit;
  readonly confidence: {
    /** |grid.meanResidual - half.meanResidual| in beats. The two sum to 0.5 per tap, so the margin is 0 when the taps sit a quarter beat from both, and 0.5 when all taps sit exactly on one of them. */
    readonly margin: number;
    /** Number of taps the decision rests on. */
    readonly taps: number;
  };
}

/**
 * Choose between the beat grid and the same grid moved half a beat later, whichever
 * leaves the smaller mean distance to the taps. The taps are first moved back by
 * `expectedTapOffsetSec`, the latency a tap is expected to have (the app's calibration
 * value; 0 when there is none). Pass every tap available: the margin shrinks with few
 * taps, with scattered taps, and with taps near a quarter beat from the grid, and the
 * caller can show "unsure" from `confidence`. A tie keeps the grid as tracked.
 */
export function alignToTaps(beats: readonly number[], taps: readonly number[], expectedTapOffsetSec = 0): TapAlignment {
  const grid: number[] = [];
  const half: number[] = [];
  if (beats.length >= 2) {
    for (const t of taps) {
      const x = fractionalBeat(beats, t - expectedTapOffsetSec);
      grid.push(x - Math.round(x));
      const h = x - 0.5;
      half.push(h - Math.round(h));
    }
  }
  const mean = (r: readonly number[]) => (r.length ? r.reduce((a, b) => a + Math.abs(b), 0) / r.length : 0);
  const g: HypothesisFit = { meanResidual: mean(grid), residuals: grid };
  const h: HypothesisFit = { meanResidual: mean(half), residuals: half };
  const shifted = h.meanResidual < g.meanResidual;
  return {
    beats: shifted ? halfBeatShift(beats) : [...beats],
    shifted,
    grid: g,
    half: h,
    confidence: { margin: Math.abs(g.meanResidual - h.meanResidual), taps: grid.length },
  };
}

/**
 * The pulse tracker's version of `alignToTaps`: `pulses` run at twice the beat rate (a pulse on each beat and on each "&"), so there are two
 * candidate grids that sit on real onsets, the even pulses and the odd pulses, and nothing has to be moved half a beat. The one whose beats are nearer
 * the taps (mean distance, as in `alignToTaps`) is chosen; a tie keeps the even pulses. `shifted` is true when the odd pulses were chosen, so that the
 * decision reads as the half-beat decision does (`grid` is the even pulses, `half` the odd).
 */
export function alignToPulses(pulses: readonly number[], taps: readonly number[], expectedTapOffsetSec = 0): TapAlignment {
  const even = pulses.filter((_, i) => i % 2 === 0), odd = pulses.filter((_, i) => i % 2 === 1);
  const fit = (beats: readonly number[]): HypothesisFit => {
    const residuals: number[] = [];
    if (beats.length >= 2) for (const t of taps) { const x = fractionalBeat(beats, t - expectedTapOffsetSec); residuals.push(x - Math.round(x)); }
    return { meanResidual: residuals.length ? residuals.reduce((a, b) => a + Math.abs(b), 0) / residuals.length : 0, residuals };
  };
  const g = fit(even), h = fit(odd);
  const shifted = odd.length >= 2 && h.meanResidual < g.meanResidual;
  return { beats: shifted ? odd : even, shifted, grid: g, half: h, confidence: { margin: Math.abs(g.meanResidual - h.meanResidual), taps: taps.length >= 1 && even.length >= 2 ? taps.length : 0 } };
}

/** Position of t between its two surrounding beats as a fraction in [0, 1), extrapolating by the nearest interval. */
export function fractionalBeat(beats: readonly number[], t: number): number {
  const n = beats.length;
  let lo = 0;
  if (t >= beats[n - 1]!) lo = n - 2;
  else if (t > beats[0]!) {
    let a = 0, b = n - 1;
    while (b - a > 1) {
      const mid = (a + b) >> 1;
      if (beats[mid]! <= t) a = mid; else b = mid;
    }
    lo = a;
  }
  const x = (t - beats[lo]!) / (beats[lo + 1]! - beats[lo]!);
  return x - Math.floor(x);
}
